'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { Menu, X } from 'lucide-react';

export default function LandingNav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-sm shadow-sm border-b border-slate-100' : 'bg-transparent'}`}
      aria-label="Main navigation"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-2">
            <AppLogo size={36} />
            <span className="font-display font-bold text-xl text-teal-700 tracking-tight">SafePlay</span>
          </div>

          <div className="hidden md:flex items-center gap-6" role="list" aria-label="Navigation links">
            {[
              { label: 'Platforms', href: '#platforms' },
              { label: 'Features', href: '#features' },
              { label: 'Pricing', href: '#pricing' },
              { label: 'For Leagues', href: '#pricing' },
            ]?.map((item) => (
              <a
                key={`nav-${item?.label}`}
                href={item?.href}
                role="listitem"
                className="text-sm font-medium text-slate-600 hover:text-teal-600 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 rounded"
              >
                {item?.label}
              </a>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <Link
              href="/sign-up-login-screen"
              className="text-sm font-semibold text-teal-700 hover:text-teal-800 transition-colors px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-teal-500 rounded"
            >
              Sign In
            </Link>
            <Link
              href="/sign-up-login-screen"
              className="text-sm font-semibold bg-teal-700 hover:bg-teal-800 text-white px-4 py-2 rounded-lg transition-all duration-150 active:scale-95 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
            >
              Get Started Free
            </Link>
          </div>

          <button
            className="md:hidden p-2 rounded-lg hover:bg-slate-100 text-slate-600 focus:outline-none focus:ring-2 focus:ring-teal-500"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={mobileOpen}
            aria-controls="mobile-menu"
          >
            {mobileOpen ? <X size={20} aria-hidden="true" /> : <Menu size={20} aria-hidden="true" />}
          </button>
        </div>
      </div>
      {/* Mobile menu */}
      {mobileOpen && (
        <div id="mobile-menu" className="md:hidden bg-white border-t border-slate-100 px-4 py-4 space-y-3 animate-fade-in" role="navigation" aria-label="Mobile navigation">
          {[
            { label: 'Platforms', href: '#platforms' },
            { label: 'Features', href: '#features' },
            { label: 'Pricing', href: '#pricing' },
            { label: 'For Leagues', href: '#pricing' },
          ]?.map((item) => (
            <a
              key={`mob-${item?.label}`}
              href={item?.href}
              className="block text-sm font-medium text-slate-600 py-1.5 focus:outline-none focus:ring-2 focus:ring-teal-500 rounded"
              onClick={() => setMobileOpen(false)}
            >
              {item?.label}
            </a>
          ))}
          <div className="pt-3 border-t border-slate-100 flex flex-col gap-2">
            <Link href="/sign-up-login-screen" className="text-center text-sm font-semibold text-teal-700 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 rounded">
              Sign In
            </Link>
            <Link href="/sign-up-login-screen" className="text-center text-sm font-semibold bg-teal-700 text-white py-2.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2">
              Get Started Free
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}